<template>
  <simple-scrollable-modal class="add-items-modal">
    <template #title>Agregar items</template>
    <template #content>
      <v-autocomplete
        v-if="!edit"
        :items="resumedItems"
        :loading="searching"
        :search-input.sync="search"
        v-model="searchSelection"
        ref="autocomplete"
        single-line
        prepend-icon="mdi-magnify"
        clearable
        background-color="#fff"
        outlined
        rounded
        dense
        hide-details
        hide-selected
        item-text="name"
        item-value="id"
        label="Nombre o id de producto..."
        @change="selectItem"
        return-object
      >
        <template v-slot:no-data>
          <v-list-item>
            <v-list-item-title> Ingrese producto a buscar </v-list-item-title>
          </v-list-item>
        </template>
        <template v-slot:item="{ item }">
          <v-list-item-avatar v-if="item.picture">
            <v-img
              class="picture"
              :src="`${uploadsPath}/${item.picture}`"
            ></v-img>
          </v-list-item-avatar>
          <v-list-item-content>
            <v-list-item-title v-text="item.name"></v-list-item-title>
          </v-list-item-content>
        </template>
      </v-autocomplete>

      <div
        v-if="!selectedCategory"
        class="categories d-flex flex-row justify-space-around flex-wrap"
      >
        <div
          class="category"
          v-for="category of fullCategories"
          :key="category.id"
          @click="selectedCategory = category"
        >
          <span class="name">{{ category.name }}</span>
        </div>
      </div>
      <div class="outer" v-else>
        <v-chip
          color="primary"
          style="cursor: pointer"
          large
          class="mb-1 mt-2"
          close
          @click="selectedCategory = null"
          @click:close="selectedCategory = null"
          >{{ selectedCategory.name }}</v-chip
        >
        <div class="items d-flex flex-row justify-space-around flex-wrap">
          <div
            class="item"
            v-for="item of categoryItems"
            :key="item.id"
            @click="addItem(item)"
          >
            <span class="name">{{ item.name }}</span>
          </div>
        </div>
      </div>
    </template>
    <template #action-bar>
      <div class="d-flex justify-space-between align-center">
        <!-- <div
          class="totals d-flex justify-space-between align-center"
          v-if="totals"
          @click="$emit('close')"
        >
          <span class="mr-2">{{ totals.totalCount }} items</span>
          <span class="mr-2">-</span>
          <span>Total {{ currency.symbol }}{{ totals.totalPrice }}</span>
        </div> -->
        <v-spacer />
        <v-btn @click="$emit('close')" depressed color="info" rounded
          >Cerrar</v-btn
        >
      </div>
    </template>
  </simple-scrollable-modal>
</template>

<script lang="ts">
  import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
  import SimpleScrollableModal from '../base/SimpleScrollableModal.vue';
  import { IRestaurant } from '../../../interfaces/IRestaurant';
  import { IBranch } from '../../../interfaces/IBranch';
  import { ICurrency } from '../../../interfaces/ICurrency';
  import { IRestaurantSettings } from '../../../api/services/restaurants.service';
  import { IOrderItem } from '../../../interfaces/IOrderItem';
  import { vueData } from '../../../VueData';
  import { AlertType } from '../../../interfaces/IAlert';
  import {
    ICategorySearchResumed,
    ICategorySearchResumedWithItems,
  } from '../../../interfaces/ICategorySearchResumed';
  import { IItemSearchResumed } from '../../../interfaces/IItemSearchResumed';
  import AddItemModal from './AddItemModal.vue';
  import { IItem } from '../../../interfaces/IItem';
  import { debounce } from '../../../utils';
  import ItemsService from '../../../api/services/items.service';
  import { Globals } from '../../../modules/Globals';

  @Component({
    components: { SimpleScrollableModal },
  })
  export default class AddItemsModal extends Vue {
    @Prop() restaurant: IRestaurant;
    @Prop() branch: IBranch;
    @Prop() currency: ICurrency;
    @Prop() restaurantSettings: IRestaurantSettings;
    @Prop() edit: IOrderItem;
    @Prop() menuId: number | null;
    @Prop() totals: {
      totalPrice: number;
      totalCount: number;
      taxAmount: number;
      subTotal: number;
    };
    uploadsPath = Globals.uploadsPath;
    itemsService = new ItemsService();
    categoriesCache: Map<number | null, ICategorySearchResumed[]> = null;
    itemsCache: Map<number | null, IItemSearchResumed[]> = null;
    selectedCategory: ICategorySearchResumedWithItems = null;

    async created() {
      this.doSearch = debounce(() => {
        this.searchItems();
      }, 50);

      this.init();
    }

    init() {
      const { itemsCache, categoriesCache } = vueData;
      if (
        !itemsCache?.has(this.menuId ? +this.menuId : null) ||
        !categoriesCache?.has(this.menuId ? +this.menuId : null)
      ) {
        vueData.addAlert(
          'Ocurrió un error, por favor actualice la página',
          AlertType.Error
        );
        this.$emit('close');
        return;
      }

      this.itemsCache = itemsCache;
      this.categoriesCache = categoriesCache;
    }

    addItem(item: IItemSearchResumed) {
      this.$modal.show(
        AddItemModal,
        {
          itemId: item.id,
          restaurant: this.restaurant,
          branch: this.branch,
          currency: this.currency,
          restaurantSettings: this.restaurantSettings,
        },
        {
          name: 'add-item',
          adaptive: true,
          classes: 'dialog--height-limit',
          height: 'auto',
          //width: 1000,
          scrollable: true,
          maxWidth: window.innerWidth * 0.95,
          transition: 'none',
        },
        {
          add: (newOI: IOrderItem) => {
            this.$parent.$emit('add', newOI);
          },
        }
      );
    }

    get fullCategories() {
      if (!this.itemsCache || !this.categoriesCache) return [];

      const categories = this.categoriesCache.get(
          this.menuId ? +this.menuId : null
        ),
        items = this.itemsCache.get(this.menuId ? +this.menuId : null);

      const isFull = (
        cat: ICategorySearchResumed
      ): cat is ICategorySearchResumedWithItems => {
        return 'items' in cat;
      };

      const fullCats: ICategorySearchResumedWithItems[] = [];

      for (const cat of categories) {
        if (!isFull(cat)) {
          const fullCat = <ICategorySearchResumedWithItems>cat;
          fullCat.items = items.filter((i) => i.category.id === cat.id);
        }
        fullCats.push(<ICategorySearchResumedWithItems>cat);
      }

      return fullCats;
    }

    get categoryItems() {
      return this.selectedCategory?.items;
    }

    /* SEARCH */

    search: string = null;
    searchSelection: IItem = null;
    doSearch: () => void;
    resumedItems: IItemSearchResumed[] = [];
    searching: boolean = false;

    @Watch('search')
    onSearchChanged() {
      this.doSearch();
    }

    async searchItems() {
      this.resumedItems = [];

      if (!this.search) return;

      try {
        this.resumedItems = vueData.itemsCache
          .get(this.menuId ? +this.menuId : null)
          .filter((i) =>
            i.name.toLowerCase().includes(this.search.toLowerCase())
          );
      } catch (err) {
      } finally {
        this.searching = false;
      }
    }

    selectItem(item: IItemSearchResumed) {
      if (!item) return;
      this.addItem(item);
      Vue.nextTick(() => {
        this.search = null;
        this.searchSelection = null;
      });
    }
  }
</script>

<style lang="scss" scoped>
  .add-items-modal {
    ::v-deep .content {
      min-height: 100%;
      background-color: #f2f2f2;
      padding: 8px;
    }
  }

  .categories {
    /*background-color: #f2f2f2;*/
    padding: 8px;
    border-radius: 10px;

    .category {
      cursor: pointer;
      padding: 18px 20px;
      margin: 5px;
      background-color: #ffffff;
      border-radius: 5px;
      flex: 1 1 auto !important;
      text-align: center;
      max-width: 33.3%;
      box-shadow: 0 1px 5px -2px rgb(0 0 0 / 30%);
      color: #7b7b7b;
      display: flex;
      align-items: center;
      justify-content: center;
      /* font-weight: 500; */
      font-size: 1.1em;

      @media (max-width: 599px) {
        max-width: 50%;
      }
    }
  }
  .items {
    .item {
      cursor: pointer;
      padding: 18px 20px;
      margin: 5px;
      background-color: #ffffff;
      border-radius: 5px;
      flex: 1 1 auto !important;
      text-align: center;
      max-width: 33.3%;
      box-shadow: 0 1px 5px -2px rgb(0 0 0 / 30%);
      color: #585858;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: 400;
      font-size: 0.95em;

      @media (max-width: 599px) {
        max-width: 50%;
      }
    }
  }

  .totals {
    background-color: #ff8b30;
    padding: 6px 22px;
    border-radius: 20px;
    color: #fff;
    font-weight: 300;
  }
</style>
